import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Menu, MenuItem } from "@mui/material";
import { StatusPill } from "components";
import { menuPaperSx, menuItemSx } from "components/internal/menuSx";
import { selectGameId, selectGameObject } from "features/gamestate/gameStateSlice";
import { selectAllPlayers } from "features/player/playerSlice";
import { getDisplayStatus, getPublishStatus, PUBLISH_STATUS } from "../lifecycle";
import { statusOverrideItems } from "./statusOverride";
import { liveStateLabel, controllingPlayerName } from "./liveState";
import logging from "utils/logging";
import "./StatusMenu.css";

const log = logging.getLogger("StatusMenu");

const Caret = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * The header status pill, made interactive: clicking it opens a menu to manually
 * override the game's lifecycle status (spec feedback — a power affordance for
 * hosts). Archive/restore stay in Settings; this covers draft/ready/in-progress/
 * played.
 */
const StatusMenu = () => {
  const game = useSelector(selectGameObject);
  const gameId = useSelector(selectGameId);
  const players = useSelector(selectAllPlayers);
  const [anchor, setAnchor] = useState(null);

  if (!game) return null;

  const display = getDisplayStatus(game);
  const live =
    display === PUBLISH_STATUS.IN_PROGRESS
      ? liveStateLabel(game, { selectingPlayer: controllingPlayerName(game, players) })
      : null;
  const items = statusOverrideItems(getPublishStatus(game), gameId, (err) =>
    log.error("Status override failed", err)
  );

  return (
    <>
      <button
        type="button"
        className="gx-statusmenu"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-label="Change game status"
      >
        <StatusPill status={display === "awaiting" ? "awaiting-review" : display} />
        <span className="gx-statusmenu-caret"><Caret /></span>
      </button>
      {live && <span className="gx-statusmenu-sub">{live}</span>}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: menuPaperSx } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className="gx-statusmenu-head">Override status</div>
        {items.map((item, i) => (
          <MenuItem
            key={i}
            sx={menuItemSx}
            onClick={() => {
              item.onClick();
              setAnchor(null);
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default StatusMenu;
